import { apiRequest } from '../api/client';

/**
 * Training module HTTP calls (spec "Rutinas con GIFs y progreso", plan Tasks
 * 3-5). Unlike `admin-api.js`/`member-api.js` (plain objects that hand back
 * whatever `apiRequest` resolves, leaving envelopes for the caller to
 * destructure), this module is a factory — `createTrainingApi({ fetchImpl })`
 * — and every method unwraps the server's envelope itself, returning the
 * named resource directly (a `RoutineDTO`, a `SessionDTO`, an array of
 * `ExerciseSummary`, etc.) per the Task 6 brief ("Todas vía apiRequest,
 * devuelven el objeto desenvuelto"). `fetchImpl` threads straight through to
 * `apiRequest` so tests inject a fake fetch instead of mocking globals, same
 * pattern as `client.test.js`.
 *
 * Ruling: the API (Tasks 3-5) is built in parallel and its exact response
 * envelopes for a few endpoints aren't spelled out verbatim in the plan.
 * Where the plan gives an explicit shape we use it as-is:
 *   - `GET /exercises` -> `{ exercises, total }` (the service's own return
 *     value, forwarded — the picker needs both fields, so nothing to strip).
 *   - `GET /exercises/:id` -> `{ exercise }`, `.../alternatives` -> `{ exercises }`.
 *   - `GET /me/exercises/:id/last-sets` -> `{ performedOn, sets }` verbatim
 *   - `GET /me/exercises/:id/history` -> `{ entries }`, unwrapped to the `entries` array
 *     (already the final shape, no wrapper key to remove).
 *   - Any endpoint returning a single named resource (`routine`, `item`,
 *     `session`) is unwrapped to that key, matching every sibling endpoint
 *     the plan DOES spell out for the same resource.
 *   - Any endpoint listing a named resource with no pagination total spelled
 *     out (`GET /me/sessions`, `GET /members/:id/sessions`,
 *     `GET /members/:id/routines`) is unwrapped to its plural key, matching
 *     the plan's explicit `{ routines: RoutineDTO[] }` for the sibling admin
 *     list endpoint.
 *   - Progress endpoints (`GET /me/progress`, `.../progress/exercises/:id`)
 *     return their DTO directly with no wrapper key, matching the existing
 *     `GET /dashboard` convention (`AdminDashboard.jsx` consumes it
 *     unwrapped) for a summary endpoint that isn't a named CRUD resource.
 *   - 204 responses (`clearSubstitution`, `restoreSubstitution`) resolve to
 *     `null`, same as `apiRequest` already does for any 204.
 * Cost if wrong: Tasks 7-9 (admin routine editor, alumno pages) consume this
 * exact contract; a mismatch surfaces as a shape bug once wired to the real
 * API, caught by the acceptance test (Task 10) or an integration pass.
 */
export function createTrainingApi({ fetchImpl } = {}) {
  const request = (path, options) => apiRequest(path, { ...options, fetchImpl });

  function buildQuery(params) {
    const search = new URLSearchParams();
    Object.keys(params || {}).forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') return;
      search.set(key, String(value));
    });
    const qs = search.toString();
    return qs ? `?${qs}` : '';
  }

  return {
    // ---- Catalog (Task 3) ----
    listExercises: (params) => request(`/exercises${buildQuery({ ...params, withMedia: params?.withMedia ? true : undefined })}`),
    getExercise: (id) => request(`/exercises/${encodeURIComponent(id)}`).then((r) => r.exercise),
    listAlternatives: (id) => request(`/exercises/${encodeURIComponent(id)}/alternatives`).then((r) => r.exercises),

    // ---- Alumno: rutina propia (Task 4) ----
    getMyRoutine: () => request('/me/routine').then((r) => r.routine),
    setSubstitution: (itemId, exerciseId) => request(`/me/routine/items/${encodeURIComponent(itemId)}/substitution`, {
      method: 'PUT',
      body: { exerciseId },
    }).then((r) => r.item),
    clearSubstitution: (itemId) => request(`/me/routine/items/${encodeURIComponent(itemId)}/substitution`, { method: 'DELETE' }),

    // ---- Alumno: sesiones y progreso (Task 5) ----
    startSession: ({ routineDayId, performedOn }) => request('/me/sessions', {
      method: 'POST',
      body: performedOn === undefined ? { routineDayId } : { routineDayId, performedOn },
    }).then((r) => r.session),
    saveSessionSets: (id, sets) => request(`/me/sessions/${encodeURIComponent(id)}/sets`, {
      method: 'PUT',
      body: { sets },
    }).then((r) => r.session),
    completeSession: (id) => request(`/me/sessions/${encodeURIComponent(id)}/complete`, { method: 'POST' }).then((r) => r.session),
    listMySessions: (limit) => request(`/me/sessions${buildQuery({ limit })}`).then((r) => r.sessions),
    getExerciseHistory: (exerciseId) => request(`/me/exercises/${encodeURIComponent(exerciseId)}/history`).then((r) => r.entries),
    getLastSets: (exerciseId) => request(`/me/exercises/${encodeURIComponent(exerciseId)}/last-sets`),
    getMyProgress: () => request('/me/progress'),
    getMyExerciseProgress: (exerciseId) => request(`/me/progress/exercises/${encodeURIComponent(exerciseId)}`),

    // ---- Admin: rutinas y progreso de un socio (Tasks 4-5) ----
    listMemberRoutines: (memberId) => request(`/members/${encodeURIComponent(memberId)}/routines`).then((r) => r.routines),
    createRoutine: (memberId, body) => request(`/members/${encodeURIComponent(memberId)}/routines`, {
      method: 'POST',
      body,
    }).then((r) => r.routine),
    updateRoutine: (id, body) => request(`/routines/${encodeURIComponent(id)}`, { method: 'PUT', body }).then((r) => r.routine),
    archiveRoutine: (id) => request(`/routines/${encodeURIComponent(id)}/archive`, { method: 'POST' }).then((r) => r.routine),
    restoreSubstitution: (routineId, itemId) => request(
      `/routines/${encodeURIComponent(routineId)}/items/${encodeURIComponent(itemId)}/substitution`,
      { method: 'DELETE' },
    ),
    getMemberProgress: (memberId) => request(`/members/${encodeURIComponent(memberId)}/progress`),
    getMemberExerciseProgress: (memberId, exerciseId) => request(
      `/members/${encodeURIComponent(memberId)}/progress/exercises/${encodeURIComponent(exerciseId)}`,
    ),
    listMemberSessions: (memberId) => request(`/members/${encodeURIComponent(memberId)}/sessions`).then((r) => r.sessions),
  };
}
